import { Box, IconButton, styled } from '@mui/material';

/**
 * Shared sticky-table chrome for Scorecard pages. Reused by:
 *   - `EditScorecard/MetricGoalEditor.styled.ts` (re-exported as is)
 *   - `ViewScorecard/AllocatedProjects/AllocatedProjects.styled.ts`
 *
 * Pattern:
 *   - White card with `#E7E8E9` border + horizontal scroll wrapper.
 *   - 64px header row, 12px uppercase grey labels (`#6E7071`).
 *   - 56px body rows with `#FAFAFA` cell background + 8px white border-top as
 *     the visual row gap (`backgroundClip: padding-box` to keep colour clean).
 *   - First `<th>` of each body row is sticky-left (`#F6F7F7`, bold).
 *   - Last cell rounds its right corners.
 */
const STICKY_SHADOW =
  '1px 0 1px 0 rgba(118,134,145,0.13), 2px 0 5px 1px rgba(118,134,145,0.10)';

export const TableWrapper = styled(Box)({
  background: '#FFFFFF',
  border: '1px solid #E7E8E9',
  overflowX: 'auto',
  position: 'relative',
});

export const Table = styled('table')({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  fontSize: 14,
  lineHeight: '22px',
  color: '#010203',
  // Header row
  '& thead th': {
    height: 64,
    padding: '12px 16px',
    background: '#FFFFFF',
    borderBottom: '1px solid #E7E8E9',
    textAlign: 'left',
    verticalAlign: 'bottom',
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '20px',
    color: '#6E7071',
    textTransform: 'uppercase',
    letterSpacing: 0,
    whiteSpace: 'nowrap',
  },
  '& thead th:first-of-type': {
    paddingTop: 32,
    position: 'sticky',
    left: 0,
    zIndex: 3,
    boxShadow: STICKY_SHADOW,
  },
  // Body rows — 8px white border-top as the row gap
  '& tbody td, & tbody th': {
    minHeight: 56,
    padding: '8px 16px',
    background: '#FAFAFA',
    borderTop: '8px solid #FFFFFF',
    textAlign: 'left',
    verticalAlign: 'middle',
    backgroundClip: 'padding-box',
    color: '#010203',
  },
  '& tbody tr:first-of-type td, & tbody tr:first-of-type th': {
    borderTop: 'none',
  },
  '& tbody th:first-of-type': {
    background: '#F6F7F7',
    fontWeight: 700,
    color: '#010203',
    position: 'sticky',
    left: 0,
    zIndex: 2,
    boxShadow: STICKY_SHADOW,
  },
  '& tbody td:last-of-type': {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  // Compact inputs inside cells (used by MetricGoalEditor's editable rows)
  '& tbody td .MuiInputBase-root': {
    background: '#FFFFFF',
    fontSize: 14,
    minHeight: 32,
  },
  '& tbody td .MuiOutlinedInput-input': {
    padding: '4px 8px',
  },
});

export const RemoveButton = styled(IconButton)({
  width: 28,
  height: 28,
  color: '#6E7071',
  '&:hover': { color: '#FD9780' },
});

export const Dash = styled('span')({
  color: '#A8AAAC',
});
